import { appConfirm } from "@/lib/appDialog";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import CertificateTemplatePreviews from "@/components/CertificateTemplatePreviews";
import ContractTemplatesPanel from "@/components/contracts/ContractTemplatesPanel";
import { toast } from "sonner";
import { Loader2, Mail, Video, FileSignature, Award, CheckCircle2, XCircle, Save, RotateCcw, Send, AlertTriangle, Copy, ExternalLink, X, Sparkles, Info } from "lucide-react";

export default function AdminIntegrations() {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("status"); // status | templates | contracts | certificates
  const [editing, setEditing] = useState(null); // template key
  const [testing, setTesting] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [teamsResult, setTeamsResult] = useState(null); // {result, hint}

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/admin/integrations/status"),
        api.get("/admin/email-templates"),
      ]);
      setStatus(s.data);
      setTemplates(t.data.templates || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const testTeams = async () => {
    setTesting("teams");
    setTeamsResult(null);
    try {
      const { data } = await api.post("/admin/integrations/test-teams");
      setTeamsResult(data);
      if (data.result?.sent) {
        toast.success("تم إنشاء اجتماع اختبار ✔️ — افتح النافذة أدناه للانضمام أو النسخ");
      } else {
        toast.error(data.hint?.title || `فشل: ${data.result?.reason}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(null); }
  };
  const testEmail = async () => {
    if (!emailTo) return toast.error("أدخل بريداً للاختبار");
    setTesting("email");
    try {
      const { data } = await api.post("/admin/integrations/test-email", { to: emailTo });
      if (data.result?.sent) toast.success(`أُرسل اختبار — request-id ${data.result.request_id}`);
      else toast.error(`فشل: ${data.result?.reason}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(null); }
  };

  return (
    <DashboardLayout>
      <div data-testid="admin-integrations">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إعدادات النظام</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">التكاملات والقوالب</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">إدارة اتصالات Microsoft وقوالب البريد والعقود والشهادات المنشورة في المنصة.</p>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-2">
          <button onClick={() => setTab("status")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "status" ? "bg-teal-700 text-white" : "bg-white text-slate-700 border border-slate-200"}`} data-testid="tab-status">حالة الاتصال</button>
          <button onClick={() => setTab("templates")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "templates" ? "bg-teal-700 text-white" : "bg-white text-slate-700 border border-slate-200"}`} data-testid="tab-templates">قوالب البريد</button>
          <button onClick={() => setTab("contracts")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${tab === "contracts" ? "bg-teal-700 text-white" : "bg-white text-slate-700 border border-slate-200"}`} data-testid="tab-contracts">
            <FileSignature className="h-4 w-4" /> العقود
          </button>
          <button onClick={() => setTab("certificates")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${tab === "certificates" ? "bg-teal-700 text-white" : "bg-white text-slate-700 border border-slate-200"}`} data-testid="tab-certificates">
            <Award className="h-4 w-4" /> الشهادات
          </button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : tab === "status" ? (
          <div className="grid gap-4 md:grid-cols-2">
            <StatusCard
              icon={Mail} title="البريد (Microsoft Graph)"
              configured={status?.email?.configured}
              sub={status?.email?.sender ? `المُرسل: ${status.email.sender}` : "غير مُعدّ"}
              actions={
                <div className="flex flex-wrap gap-2">
                  <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="بريد للاختبار" className="rounded-lg border border-slate-300 px-3 py-2 text-sm flex-1 min-w-[180px]" dir="ltr" data-testid="test-email-to" />
                  <button onClick={testEmail} disabled={testing === "email"} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="test-email-btn">
                    {testing === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال اختبار
                  </button>
                </div>
              }
              testid="int-email"
            />
            <StatusCard
              icon={Video} title="Teams (Microsoft Graph)"
              configured={status?.teams?.configured}
              sub={status?.teams?.organizer_upn ? `المنظّم: ${status.teams.organizer_upn}` : "غير مُعدّ"}
              extra={status?.teams?.organizer_oid && <div className="text-[10px] text-slate-400 mt-1" dir="ltr">OID: {status.teams.organizer_oid}</div>}
              actions={
                <button onClick={testTeams} disabled={testing === "teams"} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="test-teams-btn">
                  {testing === "teams" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} إنشاء اجتماع اختبار
                </button>
              }
              testid="int-teams"
            />
          </div>
        ) : tab === "templates" ? (
          <div className="space-y-3">
            {templates.map((t) => (
              <TemplateRow key={t.key} t={t} onEdit={() => setEditing(t.key)} onReset={async () => {
                if (!await appConfirm("إعادة القالب إلى النص الافتراضي؟")) return;
                try { await api.delete(`/admin/email-templates/${t.key}`); toast.success("تمت الإعادة"); load(); }
                catch (e) { toast.error(formatApiError(e)); }
              }} />
            ))}
          </div>
        ) : tab === "contracts" ? (
          <ContractTemplatesPanel />
        ) : (
          <CertificateTemplatePreviews />
        )}

        {editing && (
          <TemplateEditor
            template={templates.find((x) => x.key === editing)}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}

        {teamsResult && !teamsResult.result?.sent && teamsResult.hint && (
          <TeamsErrorHelp data={teamsResult} onClose={() => setTeamsResult(null)} />
        )}
        {teamsResult && teamsResult.result?.sent && (
          <TeamsSuccessPanel data={teamsResult.result} onClose={() => setTeamsResult(null)} />
        )}
      </div>
    </DashboardLayout>
  );
}

function TeamsSuccessPanel({ data, onClose }) {
  const joinUrl = data.join_url;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast.success("نُسخ رابط الاجتماع");
    } catch { toast.error("تعذّر النسخ"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-emerald-200 flex flex-col" data-testid="teams-success-panel">
        <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-slate-900 text-lg">نجح إنشاء اجتماع اختبار Teams</h3>
              <div className="text-xs text-slate-500 mt-1">الاختبار تقني فقط — الاجتماع أُنشئ في خلفية Microsoft ولا يُرسل دعوات تلقائياً</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" aria-label="إغلاق"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-sm text-sky-900 flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">لماذا لا يظهر شيء بعد الضغط؟</div>
              <div className="text-xs mt-1 leading-relaxed">
                هذا زر <b>اختبار تقني</b> يتحقق أن التطبيق يستطيع إنشاء اجتماعات فقط. الاجتماعات الحقيقية تُنشأ تلقائياً حين تجدول جلسة من لوحة المعلّم أو الطالب، وستصل الدعوات بالبريد للطرفين.
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1.5">رابط الاجتماع التجريبي:</div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 font-mono break-all" dir="ltr" data-testid="teams-success-url">
              {joinUrl}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <a
              href={joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 text-sm"
              data-testid="teams-success-join"
            >
              <ExternalLink className="h-4 w-4" /> افتح الاجتماع الآن
            </a>
            <button
              onClick={copyLink}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 text-sm"
              data-testid="teams-success-copy"
            >
              <Copy className="h-4 w-4" /> نسخ الرابط
            </button>
          </div>

          <div className="text-[11px] text-slate-500 font-mono break-all" dir="ltr">
            meeting_id: {data.meeting_id}
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex justify-end">
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 text-sm">إغلاق</button>
        </div>
      </div>
    </div>
  );
}

function TeamsErrorHelp({ data, onClose }) {
  const { hint, result } = data;
  const copyAll = async () => {
    const text = (hint.powershell || []).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("نُسخت أوامر PowerShell");
    } catch {
      toast.error("تعذّر النسخ");
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col" data-testid="teams-error-help">
        <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-700 grid place-items-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-slate-900 text-lg" data-testid="teams-error-title">{hint.title}</h3>
              <div className="text-xs text-slate-500 mt-0.5 font-mono" dir="ltr">reason: {result?.reason}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" aria-label="إغلاق"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="teams-error-message">{hint.message}</p>
          {result?.detail && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 font-mono break-words" dir="ltr">
              {result.detail}
            </div>
          )}
          {hint.checklist && hint.checklist.length > 0 && (
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="teams-error-checklist">
              <div className="text-sm font-bold text-amber-900 mb-2">قائمة التحقق:</div>
              <ul className="space-y-2 text-sm text-amber-900 leading-relaxed">
                {hint.checklist.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
            </div>
          )}
          {hint.azure_portal_checklist && (
            <div className="p-4 rounded-lg bg-sky-50 border border-sky-200" data-testid="teams-azure-checklist">
              <div className="text-sm font-bold text-sky-900 mb-2">Azure Portal — API Permissions:</div>
              <ul className="space-y-1 text-sm text-sky-900 mb-2">
                {hint.azure_portal_checklist.required_permissions.map((p, i) => (
                  <li key={i} className="font-mono text-xs" dir="ltr">• {p}</li>
                ))}
              </ul>
              <div className="text-xs text-sky-800 mb-2">{hint.azure_portal_checklist.note}</div>
              <a href={hint.azure_portal_checklist.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-sky-700 hover:underline font-semibold">
                <ExternalLink className="h-3 w-3" /> فتح Azure Portal
              </a>
            </div>
          )}
          {hint.verify_commands && hint.verify_commands.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-800">أوامر التحقق (PowerShell):</div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(hint.verify_commands.join("\n"));
                      toast.success("نُسخت أوامر التحقق");
                    } catch { toast.error("تعذّر النسخ"); }
                  }}
                  className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-800 font-semibold"
                  data-testid="copy-verify"
                >
                  <Copy className="h-3.5 w-3.5" /> نسخ
                </button>
              </div>
              <pre className="p-4 rounded-lg bg-slate-900 text-teal-200 text-xs overflow-x-auto font-mono leading-relaxed" dir="ltr">
{hint.verify_commands.join("\n")}
              </pre>
            </div>
          )}
          {hint.fix_commands && hint.fix_commands.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-800">أوامر الإصلاح:</div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(hint.fix_commands.join("\n"));
                      toast.success("نُسخت أوامر الإصلاح");
                    } catch { toast.error("تعذّر النسخ"); }
                  }}
                  className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-800 font-semibold"
                  data-testid="copy-fix"
                >
                  <Copy className="h-3.5 w-3.5" /> نسخ
                </button>
              </div>
              <pre className="p-4 rounded-lg bg-slate-900 text-amber-200 text-xs overflow-x-auto font-mono leading-relaxed" dir="ltr">
{hint.fix_commands.join("\n")}
              </pre>
            </div>
          )}
          {hint.powershell && hint.powershell.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-800">أوامر PowerShell:</div>
                <button onClick={copyAll} className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-800 font-semibold" data-testid="copy-powershell">
                  <Copy className="h-3.5 w-3.5" /> نسخ الكل
                </button>
              </div>
              <pre className="p-4 rounded-lg bg-slate-900 text-teal-200 text-xs overflow-x-auto font-mono leading-relaxed" dir="ltr">
{hint.powershell.join("\n")}
              </pre>
            </div>
          )}
          {hint.docs_url && (
            <a href={hint.docs_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-teal-700 hover:underline font-semibold" data-testid="teams-docs-link">
              <ExternalLink className="h-3.5 w-3.5" /> توثيق Microsoft الرسمي
            </a>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end">
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600">فهمت</button>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ icon: Icon, title, configured, sub, actions, extra, testid }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-teal-50 text-teal-700 grid place-items-center"><Icon className="h-5 w-5" /></div>
          <div>
            <h3 className="font-heading font-bold text-slate-900">{title}</h3>
            <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
            {extra}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${configured ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-red-50 text-red-700 border-red-100"}`}>
          {configured ? <><CheckCircle2 className="h-3.5 w-3.5" /> مُعدّ</> : <><XCircle className="h-3.5 w-3.5" /> غير مُعدّ</>}
        </span>
      </div>
      <div className="mt-4">{actions}</div>
    </div>
  );
}

function TemplateRow({ t, onEdit, onReset }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 flex items-center justify-between gap-3" data-testid={`tpl-${t.key}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-heading font-bold text-slate-900">{t.label}</div>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" dir="ltr">{t.key}</span>
          {t.overridden && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">مُعدَّل</span>}
          {!t.enabled && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700">معطّل</span>}
        </div>
        <div className="text-xs text-slate-500 mt-1 truncate">{t.subject}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onEdit} className="px-3 py-2 rounded-lg bg-teal-700 text-white text-xs font-semibold hover:bg-teal-600" data-testid={`edit-tpl-${t.key}`}>تحرير</button>
        {t.overridden && <button onClick={onReset} className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200" title="إعادة للنص الافتراضي"><RotateCcw className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}

function TemplateEditor({ template, onClose, onSaved }) {
  const [subject, setSubject] = useState(template.subject);
  const [html, setHtml] = useState(template.html);
  const [enabled, setEnabled] = useState(template.enabled);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/email-templates/${template.key}`, { subject, html, enabled });
      toast.success("تم حفظ القالب");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col" data-testid="tpl-editor-modal">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-slate-900 text-lg">تحرير قالب: {template.label}</h3>
            <div className="text-xs text-slate-500 mt-0.5">استخدم متغيّرات مثل <code>{'{{name}}'}</code> <code>{'{{link}}'}</code> ليتم استبدالها تلقائياً</div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="tpl-enabled" />
            مفعّل
          </label>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">عنوان الرسالة</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="tpl-subject" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">HTML</label>
              <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={18} dir="ltr" className="w-full rounded-lg border border-slate-300 px-4 py-3 font-mono text-xs focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="tpl-html" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">معاينة</label>
              <iframe title="preview" srcDoc={html} className="w-full h-[420px] rounded-lg border border-slate-300 bg-white" />
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="tpl-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ
          </button>
        </div>
      </div>
    </div>
  );
}
